/**
 * Whether the active property's prices are being recalculated — one answer
 * per tab, for every screen that has to wait for it.
 *
 * A strategy save starts a full recalculation, and the server writes it a
 * 30-day chunk at a time, so a Prices page opened mid-run showed the first
 * weeks recalculated and the rest not. The only thing that knew a run was in
 * flight was the promise chain inside the Strategies page, which told a toast
 * and never the grid: the operator read a half-written plan, saw "Prices
 * refreshed." in the corner, and still had to reload for the new prices.
 *
 * So the knowledge lives here, beside the requests, in the module-level
 * style of `syncStatusEvents.ts`. A start marks the run the moment it is
 * asked for, before the request has reached the server, and the Prices page
 * opened a moment later already knows. A page that did not start the run —
 * after a reload, in a second tab, behind an admin's recalculation or a
 * revealed room kind — asks the server with `watchPriceGeneration`. There is
 * only ever one wait per tab, which is what makes the toast and the grid
 * change at the same moment.
 *
 * Each start takes the wait over from the one before it rather than joining
 * it. "Switch back" on the save toast is a second start that can land after
 * the save's run has finished but before the server has heard of the second:
 * a wait left running would read that finished run as the answer, and the one
 * the server then starts would run with nobody watching it. The superseded
 * start's promise rejects with an `AbortError`, which its caller reads as
 * "somebody else reports this now".
 *
 * Nothing here raises a toast — a run this tab only watched is announced by
 * the screen waiting for it, and the words belong to whoever has them.
 */

import {
    fetchGenerationJobStatus,
    triggerFullRegeneration,
    waitForGenerationComplete,
    type GenerationJob,
} from "./pricingApi";
import { ActiveConnectionChangedError } from "./apiClient";
import { isAbortError } from "./jobPolling";
import type { LoadedConnectionId } from "./loadedConnection";

export type PriceGenerationStatus = "idle" | "running" | "completed" | "error";

export interface PriceGenerationSnapshot {
    status: PriceGenerationStatus;
    /** The chunk the pass now running is on (the server counts from 1). */
    currentChunk: number;
    totalChunks: number;
    /** The run has outlasted the point where it counts as slow. */
    slow: boolean;
}

export type PriceGenerationOutcome = "completed" | "error";

const IDLE: PriceGenerationSnapshot = { status: "idle", currentChunk: 0, totalChunks: 0, slow: false };

let snapshot: PriceGenerationSnapshot = IDLE;
const storeListeners = new Set<() => void>();
const settledListeners = new Set<(outcome: PriceGenerationOutcome) => void>();

/** Bumped by every start and by a reset: a wait that is not the latest speaks for nobody. */
let generation = 0;
/** The wait in progress, if any — one per tab. */
let activeWait: AbortController | null = null;
/** Whether this tab has read the server's status since it last started over. */
let hasLooked = false;
/** The settled run this tab already knows about, so it is not news twice. */
let knownSettledRun: string | null = null;

function setSnapshot(next: PriceGenerationSnapshot): void {
    if (
        next.status === snapshot.status
        && next.currentChunk === snapshot.currentChunk
        && next.totalChunks === snapshot.totalChunks
        && next.slow === snapshot.slow
    ) {
        return;
    }
    snapshot = next;
    for (const listener of [...storeListeners]) listener();
}

function runKey(job: GenerationJob): string {
    return `${job.jobId}|${job.completedAt ?? ""}`;
}

function settle(outcome: PriceGenerationOutcome): void {
    setSnapshot({ ...snapshot, status: outcome, slow: false });
    // Synchronously, in the same task as the store change: a screen that
    // reloads here lands its loading state in the render the store change
    // causes, so the grid it hid never paints its old numbers for a frame.
    for (const listener of [...settledListeners]) listener(outcome);
}

function progressOf(job: GenerationJob): Pick<PriceGenerationSnapshot, "currentChunk" | "totalChunks"> {
    return { currentChunk: job.currentChunk ?? 0, totalChunks: job.totalChunks ?? 0 };
}

function stopWaiting(): void {
    activeWait?.abort();
    activeWait = null;
}

/**
 * How long the page waits on a run before it stops claiming anything about it.
 * A run prices the whole plan, a year and a half since Oct 2026: half as much
 * again as the year the default five minutes were sized for, so the wait grows
 * with it. The server's own deadline scales with the run's chunks
 * (`priceGenerationJobs.ts`).
 */
const FULL_PASS_MAX_WAIT_MS = 8 * 60 * 1_000;

/** Wait on the server's job for the start (or watch) numbered `ticket`. */
function waitFor(ticket: number, onSlow?: () => void): Promise<GenerationJob> {
    const controller = new AbortController();
    activeWait = controller;
    const isCurrent = () => ticket === generation && activeWait === controller;

    return waitForGenerationComplete({
        signal: controller.signal,
        maxWaitMs: FULL_PASS_MAX_WAIT_MS,
        onProgress: (job) => {
            if (isCurrent()) setSnapshot({ ...snapshot, status: "running", ...progressOf(job) });
        },
        onSlow: () => {
            if (!isCurrent()) return;
            setSnapshot({ ...snapshot, slow: true });
            onSlow?.();
        },
    }).then(
        (job) => {
            if (!isCurrent()) throw new DOMException("A newer wait took over", "AbortError");
            activeWait = null;
            knownSettledRun = runKey(job);
            settle(job.status === "completed" ? "completed" : "error");
            return job;
        },
        (err: unknown) => {
            if (isCurrent()) {
                activeWait = null;
                // Gave up waiting — the deadline, or a status that stopped
                // answering. The page cannot claim the prices are ready.
                if (!isAbortError(err)) settle("error");
            }
            throw err;
        },
    );
}

/**
 * Recalculate the active property's prices and follow the run until it ends.
 *
 * `connectionId` names the property the caller's data was read for — the
 * strategy page's strategies, whose save the run follows — and the service
 * refuses the run once another property is open
 * (`ActiveConnectionChangedError`).
 *
 * Resolves with the settled job, completed or failed alike — its own state
 * says which. Rejects when the start is refused, when the wait gives up, and
 * with an `AbortError` when a newer start (or a property switch) took over.
 */
export async function startTrackedGeneration(
    options: { onSlow?: () => void; connectionId?: LoadedConnectionId } = {},
): Promise<GenerationJob> {
    const ticket = ++generation;
    stopWaiting();
    setSnapshot({ status: "running", currentChunk: 0, totalChunks: 0, slow: false });

    try {
        await triggerFullRegeneration(options.connectionId);
    } catch (err) {
        if (ticket === generation) {
            // Refused, so this start runs nothing — but a run already in
            // flight still is, and the server is the one to ask. Unless the
            // refusal was for another property being open: the run it would
            // answer about is that property's, not this page's.
            setSnapshot(IDLE);
            if (!(err instanceof ActiveConnectionChangedError)) void watchPriceGeneration();
        }
        throw err;
    }

    if (ticket !== generation) {
        throw new DOMException("A newer start took over", "AbortError");
    }
    return waitFor(ticket, options.onSlow);
}

/**
 * Ask the server whether a run is going, and follow it if one is.
 *
 * For the screens that did not start the run. Does nothing while this tab is
 * already waiting on one, and reads a status it may not see — a read-only
 * viewer's, or a blip — as nothing to show. A run that finished since this tab
 * last looked is reported as settled, except on the first look: whatever that
 * run wrote is what the screen asking has just loaded.
 */
export async function watchPriceGeneration(): Promise<void> {
    if (activeWait) return;
    const ticket = generation;

    let job: GenerationJob | null;
    try {
        job = await fetchGenerationJobStatus();
    } catch {
        return;
    }
    // A start or a reset got in while the status was on its way.
    if (ticket !== generation || activeWait) return;

    const firstLook = !hasLooked;
    hasLooked = true;
    if (!job) return;

    if (job.status === "running") {
        setSnapshot({ status: "running", ...progressOf(job), slow: false });
        void waitFor(ticket).catch(() => { });
        return;
    }

    const key = runKey(job);
    if (key === knownSettledRun) return;
    knownSettledRun = key;
    if (!firstLook) settle(job.status === "completed" ? "completed" : "error");
}

/** Forget everything — the active property changed, and its runs are not this one's. */
export function resetPriceGeneration(): void {
    generation++;
    stopWaiting();
    hasLooked = false;
    knownSettledRun = null;
    setSnapshot(IDLE);
}

/** The operator has read how the last run ended. A run still going stays. */
export function dismissPriceGenerationOutcome(): void {
    if (snapshot.status === "completed" || snapshot.status === "error") setSnapshot(IDLE);
}

export function getPriceGenerationSnapshot(): PriceGenerationSnapshot {
    return snapshot;
}

/** For `useSyncExternalStore`. Returns the unsubscribe function. */
export function subscribePriceGeneration(listener: () => void): () => void {
    storeListeners.add(listener);
    return () => {
        storeListeners.delete(listener);
    };
}

/**
 * Called once each time a run ends, completed or failed — including one that
 * ended while this tab was looking elsewhere. Returns the unsubscribe function.
 */
export function onPriceGenerationSettled(listener: (outcome: PriceGenerationOutcome) => void): () => void {
    settledListeners.add(listener);
    return () => {
        settledListeners.delete(listener);
    };
}
